<script setup lang="ts">
import { onBeforeMount, ref } from "vue";
import { useI18n } from "vue-i18n";

import { isset } from "@/helpers/object";
import { match } from "@/helpers/string";
import { useConfigurationStore } from "@/store/configurationStore";

import type { supporttedEventNames } from "@/dsComponents/Dropdown/types";
import type { LabelValue } from '@/types/common';
import type { IHabilitationDocument } from '@/types/documents';

import { DropdownInput, DropdownItem } from "@/dsComponents/Dropdown";
import ControlledDropdown from "@/dsComponents/Dropdown/ControlledDropdown.vue";
import ComboTagInput from "@/dsComponents/inputs/ComboTagInput.vue";

interface HabilitationsDropdownProps {
    modelValue: IHabilitationDocument[];
    placeholder?: string;
    icon?: string;
    width?: string;
    height?: string;
    showIcon?: boolean;
    fitContent?: boolean;
    fullWidth?: boolean;
}

const props = withDefaults(defineProps<HabilitationsDropdownProps>(), {
    icon: "doc-certif",
    width: "320px",
    height: "220px",
    showIcon: true,
    fitContent: true,
    fullWidth: false,
});

const emit = defineEmits<(event: "update:modelValue", value: IHabilitationDocument[]) => void>();

const { t } = useI18n();
const configStore = useConfigurationStore();

const habilitations = ref<IHabilitationDocument[]>([]);
const dropdownHabilitation = ref<IHabilitationDocument[]>([]);
const habilitationSearch = ref("");

const open = ref(false);

const toggle = () => {
    open.value = !open.value;
};

const handleControlledDropdownChange = (event: supporttedEventNames) => {
    if (event === "clickaway") open.value = false;
};

const updateDropdownOptions = (items: IHabilitationDocument[]) => {
    const filtredHabilitation = habilitations.value
        .filter((habilitation) => {
            const itemNotSelected = !items.map((v) => v.value).includes(habilitation.value);
            if (!habilitationSearch.value.trim()) return itemNotSelected;
            return match(habilitation.label, habilitationSearch.value) && itemNotSelected;
        })
        .sort((a: IHabilitationDocument, b: IHabilitationDocument) => (a.label > b.label ? 1 : -1));
    dropdownHabilitation.value = filtredHabilitation;
};

const handleHabilitationSelect = (habilitation: string) => {
    const found = habilitations.value.find((hab: LabelValue) => hab.value === habilitation);
    if (found) {
        const newValue = [...props.modelValue, found];
        emit("update:modelValue", newValue);
        updateDropdownOptions(newValue);
    }
};

const handleDelete = (habilitation: string) => {
    const newValue = props.modelValue.filter((v) => v.value !== habilitation);
    emit("update:modelValue", newValue);
    updateDropdownOptions(newValue);
};

onBeforeMount(() => {
    const habilitationOptions = configStore.documentTypeRefsOptions
        .filter((ct: IHabilitationDocument) => isset(ct.label))
        .sort((a: IHabilitationDocument, b: IHabilitationDocument) => (a.label > b.label ? 1 : -1));
    habilitations.value = habilitationOptions;
    dropdownHabilitation.value = habilitationOptions;
});

const handleSearchChange = (value: string) => {
    habilitationSearch.value = value;
    updateDropdownOptions(props.modelValue);
};
</script>
<template>
    <div>
        <ControlledDropdown :width="width" :height="height" :open="open" @change="handleControlledDropdownChange" :fit-content="fitContent">
            <template #trigger-zone>
                <ComboTagInput
                    :icon="showIcon ? icon : undefined"
                    :items="modelValue"
                    :placeholder="placeholder ?? t('page.order.authorizationsPlaceholder')"
                    :fullWidth="fullWidth"
                    @click="toggle"
                    @delete="handleDelete"
                />
            </template>
            <template #dropdown-content>
                <DropdownInput :focused="open" :modelValue="habilitationSearch" @update:model-value="handleSearchChange" />
                <RecycleScroller class="dropdown-content-recycler" :items="dropdownHabilitation" :item-size="42" key-field="value" v-slot="{ item }">
                    <DropdownItem @click="() => handleHabilitationSelect(item.value)">
                        {{ item.label }}
                    </DropdownItem>
                </RecycleScroller>
            </template>
        </ControlledDropdown>
    </div>
</template>
<style scoped lang="scss">
.habilitation-container {
    padding-top: 4px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    overflow-y: auto;
}
.dropdown-content-recycler {
    height: 100%;
}
</style>
